import React, { useState, useRef, ChangeEvent, DragEvent } from 'react';
import { Upload, X } from 'lucide-react';
import { useTranslate } from '@translations/useTranslate';
import styles from './CustomUpload.module.scss';

interface FileUploadProps {
  onFileSelect?: (file: File | null) => void;
  required?: boolean;
  nationality?: string | null;
  error?: string;
  touched?: boolean;
  name?: string;
}

export const CustomFileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  required,
  nationality,
  error,
  touched,
  name,
}) => {
  const { t } = useTranslate();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = '.pdf,.png,.jpg,.jpeg';
  const acceptedMimeTypes = ['application/pdf', 'image/png', 'image/jpeg'];
  const MAX_FILE_SIZE = 1048576; // 1MB in bytes

  const label = nationality !== 'SAUDI' ? t('dsr.form.file.label1') : t('dsr.form.file.label2');

  const validateFile = (file: File): boolean => {
    if (!acceptedMimeTypes.includes(file.type)) {
      alert('Please upload only PDF, PNG or JPEG files');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert('File size must be less than 1MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile && !validateFile(selectedFile)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFile(selectedFile);
    onFileSelect?.(selectedFile);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0] || null;

    if (droppedFile && !validateFile(droppedFile)) {
      return;
    }

    setFile(droppedFile);
    onFileSelect?.(droppedFile);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleRemoveFile = () => {
    setFile(null);
    onFileSelect?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showError = touched && !file && error;

  return (
    <div className={styles['form-group']}>
      <label className={styles['form-group__label']}>
        {required && <span className={styles['form-group__label_required']}>* </span>}
        {label}
      </label>
      <div className={styles['file-upload']}>
        {!file ? (
          <div
            className={`${styles['file-upload__box']} ${
              showError ? styles['file-upload__box--error'] : ''
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className={styles['file-upload__input']}
              accept={acceptedTypes}
              name={name}
            />
            <div className={styles['file-upload__content']}>
              <Upload className={styles['file-upload__icon']} strokeWidth={1.5} />
              <p className={styles['file-upload__text']}>
                Drop files here or click to upload (PDF, PNG, JPEG, max 1MB)
              </p>
            </div>
          </div>
        ) : (
          <div className={styles['file-upload__preview']}>
            <div className={styles['file-upload__preview-content']}>
              <span className={styles['file-upload__filename']}>{file.name}</span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className={styles['file-upload__remove']}
              >
                <X className={styles['file-upload__remove-icon']} size={16} />
              </button>
            </div>
          </div>
        )}

        {showError && <div className={styles['file-upload__error']}>{error}</div>}
      </div>
    </div>
  );
};
